<template>
  <v-flex class="text-body-2">
    <v-layout column>
      <v-layout v-if="moveInCostLoading" justify-center>
        <v-progress-circular
          :size="60"
          :width="6"
          indeterminate
          class="mt-5 mb-4"
        />
      </v-layout>
      <v-flex column v-else>
        <v-flex xs11>
          <v-card>
            <v-toolbar class="text-h6 mb-3">Total</v-toolbar>
            <charge-breakdown />
          </v-card>
        </v-flex>
        <v-flex class="pl-1 mt-3 mb-2">
          <v-flex class="subheading font-weight-bold mb-1">Available Incentives</v-flex>
          <v-layout column class="text-body-1">
            <v-layout class="mt-1" align-center>
              <v-flex xs6>Amazon.com Gift Card*:</v-flex>
              <v-flex xs2>${{ giftCardAmount }}</v-flex>
              <v-flex xs3>
                <v-btn
                  data-testid="applied-gift-card-btn"
                  class="applied-gift-card-btn"
                  small
                  color="green"
                  @click="gcApplied = !gcApplied"
                  text
                  v-if="gcApplied"
                  block
                >
                  Applied <v-icon class="pl-1">check</v-icon>
                </v-btn>
                <v-btn
                  data-testid="apply-gift-card-btn"
                  class="apply-gift-card-btn"
                  small
                  color="red"
                  @click="gcApplied = !gcApplied"
                  v-if="!gcApplied"
                  block
                >
                  Apply
                </v-btn>
              </v-flex>
            </v-layout>
            <v-layout class="mt-1">
              <v-flex xs11>
                <small>
                  * Valid for new customers only. You must continuously rent your storage unit for 30 days to receive
                  your Amazon.com Gift Card. The 30 days is counted from the beginning of you rental start date. You'll
                  receive your Amazon.com Gift Card 1 to 3 weeks after reaching the 30 day threshold. Your Amazon.com
                  Gift Card will be sent to the e-mail address we have on file.
                </small>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-flex>
        <v-flex class="pl-1 mt-3 mb-2">
          <v-flex class="subheading font-weight-bold mb-1">Rental Settings</v-flex>
          <v-layout column class="text-body-1">
            <v-layout class="mt-1">
              <v-flex xs6>Move-in Date:</v-flex>
              <v-flex xs6>{{ formatDate(options.moveInDate) }}</v-flex>
            </v-layout>
            <v-layout class="mt-1">
              <v-flex xs6>Automatic Payments:</v-flex>
              <v-flex xs6>
                <span v-if="options.automaticPayments === '1'" class="success--text">Enrolled</span>
                <span v-else class="error--text">Declined</span>
              </v-flex>
            </v-layout>
            <div v-if="promotionOffers && Object.keys(promotionOffers).length">
              <v-layout class="mt-1">
                <v-flex xs6>Promotion Offered:</v-flex>
                <v-flex xs6>{{
                promotionOffers.this_month ?
                promotionOffers.this_month.text :
                promotionOffers.next_month.text
                }}</v-flex>
              </v-layout>
              <v-layout class="mt-1">
                <v-flex xs6>Promotion Begins:</v-flex>
                <v-flex xs6>
                  <span v-if="promotionMonth">{{ promotionMonth }}</span>
                  <span v-else>Not applied</span>
                </v-flex>
              </v-layout>
            </div>
          </v-layout>
        </v-flex>
        <v-flex class="pl-1 mt-4" v-if="unitFeatures || unit">
          <v-flex class="subheading font-weight-bold mb-1">Unit Details</v-flex>
          <v-layout column class="text-body-1">
            <v-layout class="mt-1" v-if="unit && unit.name">
              <v-flex xs6>Unit Number:</v-flex>
              <v-flex xs6>{{ unit.name }}</v-flex>
            </v-layout>
            <v-layout class="mt-1">
              <v-flex xs6>Unit Type:</v-flex>
              <v-flex xs6>{{ unitType }}</v-flex>
            </v-layout>
            <v-layout class="mt-1">
              <v-flex xs6>Rate:</v-flex>
              <v-flex xs6>${{ parseFloat(quotedRate).toFixed(2) }}/month</v-flex>
            </v-layout>
            <v-layout class="mt-1">
              <v-flex xs6>Unit Size:</v-flex>
              <v-flex xs6>{{ unitSize }}</v-flex>
            </v-layout>
            <v-layout class="mt-1">
              <v-flex xs6>Unit Features:</v-flex>
              <v-flex xs6>
                <v-layout
                  v-for="(feature, i) of features"
                  :key="i"
                  column
                >
                  <v-flex>{{ feature }}</v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-flex>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import ChargeBreakdown from "./ChargeBreakdown";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import { determineAmazonGiftCardAmount } from "@/helpers/gift-cards/determine-amazon-gc-amount";
import { mapState } from "vuex";

export default {
  components: { ChargeBreakdown },

  props: {
    promotionMonth: {
      type: String,
      default: ""
    },
    promotionOffers: {
      type: [Object, String]
    }
  },

  mixins: [dateMethodMixins],

  computed: {
    ...mapState({
      giftCardTiers: state => state.giftCards.giftCardTiers,
      moveInCostLoading: state => state.facilities.moveInCostLoading,
      options: state => state.tenant.options,
      unit: state => state.facilities.unit,
      quotedRate: state => state.facilities.quotedRate,
      unitFeatures: state => state.facilities.unitFeatures,
      invalidPromotion: state => state.reservations.invalidPromotion
    }),
    unitType() {
      if (this.unit && this.unit.type_name) {
        return this.unit.type_name;
      } else if (this.unitFeatures && this.unitFeatures.type_name) {
        return this.unitFeatures.type_name;
      } else {
        return "";
      }
    },
    unitSize() {
      if (this.unit && this.unit.width) {
        return "'" + parseFloat(this.unit.width) + " x '" + parseFloat(this.unit.length);
      } else if (this.unitFeatures && this.unitFeatures.width) {
        return "'" + parseFloat(this.unitFeatures.width) + " x '" + parseFloat(this.unitFeatures.length);
      } else if (this.unit && this.unit.length) {
        return "'" + parseFloat(this.unit.length) + " parking";
      } else if (this.unitFeatures) {
        return "'" + parseFloat(this.unitFeatures.length) + " parking";
      } else {
        return "";
      }
    },
    features() {
      if (this.unit && this.unit.features && this.unit.features.length) {
        return this.unit.features;
      } else if (this.unitFeatures && this.unitFeatures.features && this.unitFeatures.features.length) {
        return this.unitFeatures.features;
      } else {
        return [];
      }
    },
    giftCardAmount() {
      if (this.quotedRate) {
        return determineAmazonGiftCardAmount(this.quotedRate, this.giftCardTiers);
      }

      if (this.unit && this.unit.rate) {
        return determineAmazonGiftCardAmount(this.unit.rate, this.giftCardTiers);
      }

      return 0;
    },
    gcApplied: {
      get() {
        return this.options.giftCardEligible;
      },
      set(newValue) {
        this.$store.commit("setGiftCardEligible", newValue);
      }
    }
  },

  // ===== Lifecycle Hooks ===== //
  async created() {
    await this.$store.dispatch("getGiftCardTiers");
  }
};
</script>
